import { Injectable, inject } from '@angular/core';
import { BehaviorSubject, firstValueFrom, Observable, switchMap, tap } from 'rxjs';

import { BaseService } from './base-service';
import { ConfirmationService } from './confirmation.service';
import { ToastService } from './toast.service';
import { enrichResult } from '../utils/errors';
import { ListUsers$Params } from '@api/backend-connector/fn/admin-users/list-users';
import {
  AddUserToRoleRequest,
  AdminUserDtoPage,
  CreateUserRequest,
  RemoveUserFromRoleRequest,
  SetUserPasswordRequest,
  UpdateUserRequest,
  UserDto,
} from '@api/backend-connector/models';
import { AdminUsersApi, UsersApi } from '@api/backend-connector/services';

const toasts = {
  createSuccessSummary: 'i18n.UserToast.createSuccessSummary',
  createSuccessDetail: 'i18n.UserToast.createSuccessDetail',
  createFailSummary: 'i18n.UserToast.createFailSummary',
  createFailDetail: 'i18n.UserToast.createFailDetail',

  updateSuccessSummary: 'i18n.Toast.updateSuccessSummary',
  updateSuccessDetail: 'i18n.Toast.updateSuccessDetail',
  updateFailSummary: 'i18n.Toast.updateFailSummary',
  updateFailDetail: 'i18n.Toast.updateFailDetail',

  deleteSuccessSummary: 'i18n.UserToast.deleteSuccessSummary',
  deleteSuccessDetail: 'i18n.UserToast.deleteSuccessDetail',
  deleteFailSummary: 'i18n.UserToast.deleteFailSummary',
  deleteFailDetail: 'i18n.UserToast.deleteFailDetail',

  enabledSuccessSummary: 'i18n.UserToast.enabledSuccessSummary',
  enabledSuccessDetail: 'i18n.UserToast.enabledSuccessDetail',
  enabledFailSummary: 'i18n.UserToast.enabledFailSummary',
  enabledFailDetail: 'i18n.UserToast.enabledFailDetail',

  disabledSuccessSummary: 'i18n.UserToast.disabledSuccessSummary',
  disabledSuccessDetail: 'i18n.UserToast.disabledSuccessDetail',
  disabledFailSummary: 'i18n.UserToast.disabledFailSummary',
  disabledFailDetail: 'i18n.UserToast.disabledFailDetail',

  setPasswordSuccessSummary: 'i18n.UserToast.setPasswordSuccessSummary',
  setPasswordSuccessDetail: 'i18n.UserToast.setPasswordSuccessDetail',
  setPasswordFailSummary: 'i18n.UserToast.setPasswordFailSummary',
  setPasswordFailDetail: 'i18n.UserToast.setPasswordFailDetail',
};

export const getIsAdmin = (roles: string[]) => !!roles?.includes('admin');
export const makeUserRoles = (isAdmin: boolean) => (isAdmin ? ['admin'] : []);

export type UIUpdateUserRequest = UpdateUserRequest & { isAdmin: boolean };

@Injectable({
  providedIn: 'root',
})
export class UsersService extends BaseService {
  private readonly adminUsersApi = inject(AdminUsersApi);
  private readonly usersApi = inject(UsersApi);
  readonly toasts = toasts;

  readonly refresh$ = new BehaviorSubject<void>(undefined);

  listUsers(params: ListUsers$Params): Observable<AdminUserDtoPage> {
    return this.adminUsersApi.listUsers(params);
  }

  getUser(userId: string): Observable<UserDto> {
    return this.adminUsersApi.getUser({ id: userId });
  }

  createUser(request: CreateUserRequest) {
    return this.adminUsersApi.createUser({ body: request });
  }

  updateUser(id: string, request: UpdateUserRequest) {
    return this.adminUsersApi.updateUser({ id, body: request });
  }

  updateUserUI(id: string, request: UIUpdateUserRequest) {
    return this.adminUsersApi
      .updateUser({ id, body: request })
      .pipe(switchMap(() => this.updateIsAdmin(id, request.isAdmin)));
  }

  async enableUser(toast: ToastService, confirmation: ConfirmationService, email: string, userId: string) {
    const confirm = await confirmation.confirm({
      header: this.translateService.instant('i18n.AdminUserActions.Enable.header', { email }),
      message: this.translateService.instant('i18n.AdminUserActions.Enable.message'),
      confirmLabel: this.translateService.instant('i18n.AdminUserActions.Enable.confirm'),
    });
    if (!confirm) {
      return undefined;
    }
    return firstValueFrom(
      this.adminUsersApi.enableUser({ id: userId }).pipe(
        enrichResult(),
        tap((result) => {
          if (result.success) {
            this.refresh$.next();
            this.showEnabledSuccessToast(toast);
          } else {
            this.showEnabledFailToast(toast);
          }
        }),
      ),
    );
  }

  async disableUser(toast: ToastService, confirmation: ConfirmationService, email: string, userId: string) {
    const confirm = await confirmation.confirm({
      header: this.translateService.instant('i18n.AdminUserActions.Disable.header', { email }),
      message: this.translateService.instant('i18n.AdminUserActions.Disable.message'),
      confirmLabel: this.translateService.instant('i18n.AdminUserActions.Disable.confirm'),
    });
    if (!confirm) {
      return undefined;
    }
    return firstValueFrom(
      this.adminUsersApi.disableUser({ id: userId }).pipe(
        enrichResult(),
        tap((result) => {
          if (result.success) {
            this.refresh$.next();
            this.showDisabledSuccessToast(toast);
          } else {
            this.showDisabledFailToast(toast);
          }
        }),
      ),
    );
  }

  deleteUser(params: { userId: string }) {
    return this.adminUsersApi.deleteUser({ id: params.userId });
  }

  setUserPassword(id: string, request: SetUserPasswordRequest) {
    return this.adminUsersApi.setUserPassword({ id, body: request });
  }

  private updateIsAdmin(id: string, value: boolean) {
    return value ? this.addRole(id, { role: 'admin' }) : this.removeRole(id, { role: 'admin' });
  }

  private addRole(id: string, params: AddUserToRoleRequest) {
    return this.adminUsersApi.addUserToRole({
      id,
      body: params,
    });
  }

  private removeRole(id: string, params: RemoveUserFromRoleRequest) {
    return this.adminUsersApi.removeUserFromRole({
      id,
      body: params,
    });
  }

  showEnabledSuccessToast(toast: ToastService) {
    toast.add({
      severity: 'info',
      summary: this.toasts.enabledSuccessSummary,
      detail: this.toasts.enabledSuccessDetail,
    });
  }

  showDisabledSuccessToast(toast: ToastService) {
    toast.add({
      severity: 'info',
      summary: this.toasts.disabledSuccessSummary,
      detail: this.toasts.disabledSuccessDetail,
    });
  }

  showEnabledFailToast(toast: ToastService) {
    toast.add({
      severity: 'error',
      summary: this.toasts.enabledFailSummary,
      detail: this.toasts.enabledFailDetail,
    });
  }

  showDisabledFailToast(toast: ToastService) {
    toast.add({
      severity: 'error',
      summary: this.toasts.disabledFailSummary,
      detail: this.toasts.disabledFailDetail,
    });
  }

  showSetPasswordSuccessToast(toast: ToastService) {
    toast.add({
      severity: 'info',
      summary: this.toasts.setPasswordSuccessSummary,
      detail: this.toasts.setPasswordSuccessDetail,
    });
  }

  showSetPasswordFailToast(toast: ToastService) {
    toast.add({
      severity: 'error',
      summary: this.toasts.setPasswordFailSummary,
      detail: this.toasts.setPasswordFailDetail,
    });
  }
}
